import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './OpenSlots.css';

const POLL_MS = 10000;

/**
 * "Öppna platser" — Esportal/Esplay-style backfill browse page.
 *
 * Lists open slots in live 5v5 matches that are short a player. Any active
 * member can claim one with a single click, which adds them to the roster and
 * pushes ``matchzy_addplayer`` server-side; on success we surface the Steam
 * connect link so they can jump straight in.
 *
 * Gated to active members — the backend enforces this too, but we short-circuit
 * the UI for logged-out / pending users so the claim button never lies.
 */
export default function OpenSlots() {
  const { t } = useTranslation();
  const { isLoggedIn, isActive, getAuthHeaders } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [joined, setJoined] = useState(null); // { slotId, connect }
  const [errors, setErrors] = useState({}); // { [slotId]: message }

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/open-slots/`, {
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data) ? data : data.results || [];
        setSlots(rows.filter((s) => s.status === 'open'));
      }
    } catch (err) {
      // Best-effort poll — keep last known list on a transient failure.
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const handleClaim = useCallback(
    async (slot) => {
      setClaimingId(slot.id);
      setErrors((e) => ({ ...e, [slot.id]: '' }));
      try {
        const res = await fetch(`${API_URL}/api/open-slots/${slot.id}/claim/`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setErrors((e) => ({
            ...e,
            [slot.id]: data?.detail?.error || data?.error || t('openSlots.claimError'),
          }));
        } else {
          setJoined({ slotId: slot.id, connect: data.connect || '' });
          await refresh();
        }
      } catch (err) {
        setErrors((e) => ({ ...e, [slot.id]: t('openSlots.networkError') }));
      } finally {
        setClaimingId(null);
      }
    },
    [getAuthHeaders, refresh, t]
  );

  const mapImg = (tag) =>
    `/stats/images/maps/${tag}.${tag === 'de_train' ? 'png' : 'jpg'}`;

  return (
    <div className="open-slots-page">
      <header className="open-slots-header">
        <h1>{t('openSlots.pageTitle')}</h1>
        <p>{t('openSlots.pageSubtitle')}</p>
      </header>

      {joined && (
        <div className="open-slots-joined">
          <span className="open-slots-joined-title">{t('openSlots.joinedTitle')}</span>
          {joined.connect && (
            <a href={joined.connect} className="open-slots-connect-btn">
              {t('openSlots.connectSteam')}
            </a>
          )}
        </div>
      )}

      {!isLoggedIn && <p className="open-slots-gate">{t('openSlots.loginRequired')}</p>}
      {isLoggedIn && !isActive && (
        <p className="open-slots-gate">{t('openSlots.activeRequired')}</p>
      )}

      {loading ? (
        <div className="open-slots-loading">
          <div className="loader" />
        </div>
      ) : slots.length === 0 ? (
        <p className="open-slots-empty">{t('openSlots.empty')}</p>
      ) : (
        <div className="open-slots-grid">
          {slots.map((slot) => {
            const tag = slot.match?.maplist?.[0];
            const canClaim = isLoggedIn && isActive;
            return (
              <div key={slot.id} className="open-slots-card">
                {tag && (
                  <div className="open-slots-card-hero">
                    <img className="open-slots-card-img" src={mapImg(tag)} alt="" />
                  </div>
                )}
                <div className="open-slots-card-body">
                  <span className="open-slots-card-teams">
                    {slot.match?.team1_display_name || 'Team 1'} vs{' '}
                    {slot.match?.team2_display_name || 'Team 2'}
                  </span>
                  <span className="open-slots-card-short">
                    {t('openSlots.shortTeam', { team: slot.team_name })}
                  </span>
                  {canClaim && (
                    <button
                      type="button"
                      className="open-slots-join-btn"
                      onClick={() => handleClaim(slot)}
                      disabled={claimingId === slot.id}
                    >
                      {claimingId === slot.id
                        ? t('openSlots.joining')
                        : t('openSlots.joinBtn')}
                    </button>
                  )}
                  {errors[slot.id] && (
                    <p className="open-slots-card-error">{errors[slot.id]}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
